import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import ProjectCard from '@/components/ProjectCard';
import { supabase, type Project } from '@/lib/supabase';
import { ArrowRight } from 'lucide-react';
import { useSiteContent } from '@/hooks/useSiteContent';
import { AnimatedSection } from '@/hooks/useScrollAnimation';

const ProjectsTeaser = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const { getContentText, loading: contentLoading } = useSiteContent();

  useEffect(() => {
    fetchFeaturedProjects();
  }, []);

  const fetchFeaturedProjects = async () => {
    try {
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(3);

      if (error) throw error;
      setProjects(data || []);
    } catch (error) {
      console.error('Error fetching featured projects:', error);
    } finally {
      setLoading(false);
    }
  };

  // Dynamic grid classes based on number of items
  const getGridClasses = (count: number) => {
    if (count === 1) return 'grid grid-cols-1 max-w-md mx-auto';
    if (count === 2) return 'grid grid-cols-1 md:grid-cols-2 max-w-4xl mx-auto';
    return 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto';
  };

  return (
    <section className="py-16 md:py-20 lg:py-24 xl:py-28 section-warm relative overflow-hidden">
      {/* Subtle pattern */}
      <div className="absolute inset-0 pattern-subtle opacity-30"></div>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
        <AnimatedSection className="text-center mb-12 lg:mb-16">
          <div className="divider-elegant mx-auto mb-4 lg:mb-6"></div>
          <h2 className="font-serif text-primary mb-4">
            {getContentText('projects-teaser-title', 'Onze Projecten')}
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-base md:text-lg">
            {getContentText('projects-teaser-description', 'Van renovatie tot nieuwbouw - bekijk onze gerealiseerde vastgoedprojecten')}
          </p>
        </AnimatedSection>

        {loading || contentLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">{getContentText('projects-loading', 'Projecten laden...')}</p>
          </div>
        ) : projects.length === 0 ? (
          <AnimatedSection className="text-center py-12">
            <p className="text-muted-foreground">{getContentText('no-projects-available', 'Geen projecten beschikbaar.')}</p>
          </AnimatedSection>
        ) : (
          <div className={`${getGridClasses(projects.length)} gap-8 mb-12`}>
            {projects.map((project, index) => (
              <AnimatedSection key={project.id} delay={index * 150}>
                <ProjectCard project={project} />
              </AnimatedSection>
            ))}
          </div>
        )}

        <AnimatedSection delay={350} className="text-center">
          <a href="/projecten">
            <Button size="lg" variant="outline" className="border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground group px-8 py-6 text-base rounded-lg transition-all duration-300">
              {getContentText('all-projects-button', 'Alle Projecten')}
              <ArrowRight className="ml-2 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
            </Button>
          </a>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default ProjectsTeaser;
